import classNames from 'classnames'
import styles from './Fieldset.module.css'
import { FieldsetLegendProps, FieldsetProps } from './types'
import { LayoutCluster, LayoutStack } from '../layouts'
import { useId } from 'react'

function FieldsetLegend({ className, hint, required, children }: FieldsetLegendProps) {
  return (
    <legend className={classNames(className, styles.legend)}>
      <div className={styles.groupLabel}>
        {children}
        {required && <span aria-label="(required)">*</span>}
      </div>
      {hint ? <div className={styles.groupHint}>{hint}</div> : null}
    </legend>
  )
}

function Fieldset({
  children,
  classNames: externalStyles,
  legend,
  legendSize = 'small',
  hint,
  required,
  error,
  layout = 'horizontal',
  layoutProps = {
    space: 'none',
  },
  errorDisplay = 'all',
  invisibleLegend,
  ...props
}: FieldsetProps) {
  const layoutClasses = classNames(styles.fieldContainer, layoutProps?.className, {
    [styles.groupError!]: !!error && errorDisplay !== 'text',
  })

  const Layout = layout === 'vertical' ? LayoutStack : LayoutCluster
  const generatedId = useId()
  const id = props.id || generatedId
  const errorId = error ? `${id}-error` : undefined

  return (
    <fieldset
      id={id}
      className={classNames(styles.fieldset, externalStyles?.fieldset)}
      aria-describedby={errorId}
      {...props}
    >
      {legend || hint ? (
        <FieldsetLegend
          required={required}
          hint={hint}
          className={classNames(externalStyles?.legend, {
            [styles.legendMedium!]: legendSize === 'medium',
            [styles.invisibleLegend!]: invisibleLegend,
          })}
        >
          {legend}
        </FieldsetLegend>
      ) : null}
      <Layout className={layoutClasses} {...layoutProps}>
        {children}
      </Layout>
      {error && errorDisplay !== 'border' ? (
        <p id={errorId} className={styles.errorMessage} aria-live="polite" role="alert">
          {error}
        </p>
      ) : null}
    </fieldset>
  )
}

export default Fieldset
